import {IProductPriceObject} from '../IProduct';
import ProductModel from '../ProductModel';
import AbstractPriceModel from '../AbstractPriceModel';

/**
 * Represents Product ProductPriceModel
 */
class ProductPriceModel extends AbstractPriceModel {
	product: ProductModel;

	/**
	 * Check that product has price range within one pricebook
	 * true if variation group has variations with different prices e.g. perfumes of different volume
	 * @returns  {boolean} - product has price range
	 */
	hasPriceRange(): boolean {
		const ranges = this.product.getPriceRanges();

		return ranges.some(e => e.maxPrice > e.minPrice);
	}

	/**
	 * Returns the maximum price of the same variant used to retrieve minimal price
	 * @returns {IProductPriceObject} - the maximum price
	 */
	getMaxPrice() : IProductPriceObject {
		const variantsMaxPrice = this.product.getVariants()?.sort((a, b) => {
			return b.getPriceModel().getMinPrice().value - a.getPriceModel().getMinPrice().value;
		})
			.pop()
			?.getPriceModel()
			.getMaxPrice();

		if (variantsMaxPrice?.value) {
			return variantsMaxPrice;
		} else if (this.product.priceRanges?.length) {
			const maxPrice = this.product.priceRanges.reduce((maxPrice: IProductPriceObject, priceRange) => {
				return (priceRange.maxPrice > maxPrice) ? priceRange.maxPrice : maxPrice;
			}, 0);

			return {
				currency: this.product.currency,
				value: maxPrice,
				pricebook: this.product.priceRanges
					.find((priceRange) => priceRange.maxPrice === maxPrice).pricebook
			};
		} else {
			return this.product.getPrice();
		}
	}

	/**
	 * Returns the minimal price taking into consideration all the variants
	 * @returns {IProductPriceObject} the minimal price
	 */
	getMinPrice() : IProductPriceObject {
		const variantsMinPrice = this.product.getVariants()?.reduce(
			(minPrice: IProductPriceObject, variantModel) => {
				const currentMinPrice = variantModel.priceModel.getMinPrice();

				return (typeof minPrice?.value !== 'number' || (currentMinPrice.value < minPrice.value)) ? currentMinPrice : minPrice;
			}, null);

		return variantsMinPrice?.value ? variantsMinPrice : this.product.getPrice();
	}
}

export default ProductPriceModel;
